import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, User, Loader2 } from "lucide-react";
import { useAvailableRaynetUsers, RaynetUser } from '@/hooks/useRaynetUsers';

interface SalesRepSelectorProps {
  value: string;
  onChange: (value: string, personId?: number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const SalesRepSelector: React.FC<SalesRepSelectorProps> = ({
  value,
  onChange,
  placeholder = "Vyberte obchodního zástupce...",
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: usersResponse, isLoading } = useAvailableRaynetUsers();
  
  const users = usersResponse?.data || [];

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleSelect = (user: RaynetUser) => {
    onChange(user.username, user.person_id);
    setIsOpen(false);
  };

  const selectedUser = users.find(user => user.username === value);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        onClick={handleOpen}
        disabled={disabled}
        className={`w-full h-12 justify-between text-left font-normal ${
          value ? 'text-slate-900' : 'text-slate-500'
        } ${className}`}
      >
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-slate-400" />
          <span className="truncate">
            {selectedUser ? selectedUser.full_name : (value || placeholder)}
          </span>
        </div>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ChevronDown className="h-4 w-4 opacity-50" />
        )}
      </Button>

      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <Card className="absolute top-full left-0 right-0 mt-1 z-20 max-h-60 overflow-hidden shadow-lg border">
            <CardContent className="p-0">
              <div className="max-h-60 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Načítám uživatele...</p>
                  </div>
                ) : users.length > 0 ? (
                  <div className="py-1">
                    {users.map((user) => (
                      <Button
                        key={user.raynet_id}
                        variant="ghost"
                        className={`w-full justify-start px-4 py-3 h-auto rounded-none hover:bg-slate-50 ${
                          selectedUser?.raynet_id === user.raynet_id ? 'bg-orange-50 text-orange-700' : ''
                        }`}
                        onClick={() => handleSelect(user)}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <div className="text-left min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">
                              {user.full_name}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {user.username}
                            </div>
                          </div>
                          {user.user_role_name && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full flex-shrink-0">
                              {user.user_role_name}
                            </span>
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-500">
                    <User className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Žádní uživatelé k dispozici</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SalesRepSelector; 